// Export the DrawerComponent class for other modules to extend
export class IaDrawer extends HTMLElement {
  constructor() {
    super();
    this.id = this.dataset.drawerId;
    this.isOpen = this.classList.contains('is-open');
    this.overlay = this.querySelector('[data-drawer-overlay]');
    this.window = this.querySelector('[data-drawer-window]');

    // dynamically binded trigger
    this._boundToggle = this.toggleDrawer.bind(this);
    this.refreshTriggers();
    
    document.addEventListener('keyup', (e) => {
      if (!this.isOpen) return
      if (e.key === 'Escape') {
        this.close()
      }
    })
    
    this.overlay?.addEventListener('click', this.close.bind(this));
  }

  refreshTriggers() {
    // remove old listeners
    this.drawerOpener?.forEach((opener) => {
      opener.removeEventListener("click", this._boundToggle);
    });
  
    // reselect + bind
    this.drawerOpener = document.querySelectorAll(`[data-drawer-trigger="${this.id}"]`);
    this.drawerOpener?.forEach((opener) => {
      opener.addEventListener("click", this._boundToggle);
    });
  }

  toggleDrawer(event) {
    event.preventDefault();
    event.stopPropagation();
    this.isOpen = !this.isOpen;
    this.isOpen ? this.open() : this.close();
  }

  close() {
    const reopenDrawerId = this._reopenDrawerId;
    this._reopenDrawerId = null;
    this.classList.remove('is-open');
    this.isOpen = false;
    document.body.classList.remove('ia-overflow-hidden');
    window.iaTheme.showExtraStickyElements();
    if (reopenDrawerId) {
      const parentDrawer = document.querySelector(`[data-drawer-id="${reopenDrawerId}"]`);
      if (parentDrawer && !parentDrawer.isOpen) parentDrawer.open();
    }
  }

  open() {
    // Prevent stacking: only one extra-option drawer open at a time (fixes iOS Safari z-index/pointer-events)
    if (this.classList.contains('ia-extra-option-drawer')) {
      document.querySelectorAll('ia-drawer-component.ia-extra-option-drawer.is-open').forEach((drawer) => {
        if (drawer !== this) {
          this._reopenDrawerId = drawer.dataset.drawerId;
          drawer.close();
        }
      });
    }
    this.classList.add('is-open');
    this.isOpen = true;
    if (document.documentElement.scrollHeight > document.documentElement.clientHeight) {
      document.body.classList.add('ia-overflow-hidden');
    }
    window.iaTheme.hideExtraStickyElements();
  }
}


// Only define custom element if not already defined
if (!customElements.get('ia-drawer-component')) {
  customElements.define('ia-drawer-component', IaDrawer);
}


class IaDrawerClose extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.close.bind(this));
  }


  close() {
    const drawerId = this.dataset.drawerId;
    document.querySelector(`[data-drawer-id="${drawerId}"]`).close();
  }
}

if (!customElements.get('ia-drawer-close')) {
  customElements.define('ia-drawer-close', IaDrawerClose);
}


// Export DrawerMenu class for completeness
export class IaDrawerMenu extends IaDrawer {
  constructor() {
    super();
  }

  close() {
    super.close();
    // Close all accordions when drawer closes
    this.querySelectorAll('ia-accordion-component').forEach(accordion => {
      accordion.close();
    });
  }
}

// Only define custom element if not already defined
if (!customElements.get('ia-menu-drawer')) {
  customElements.define('ia-menu-drawer', IaDrawerMenu);
}

class IaModalComponent extends IaDrawer {
  constructor() {
    super();
  }
  
}

if (!customElements.get('ia-modal-component')) {
  customElements.define('ia-modal-component', IaModalComponent);
} 

class IaModalVideo extends IaModalComponent {
  static BREAKPOINT = 1024;

  constructor() {
    super();
    this.desktopVideo = null;
    this.mobileVideo = null;
    this.activeVideo = null;
  }

  close() {
    super.close();
    this.pauseVideos();
  }

  open() {
    super.open();
    this.playActiveVideo();
  }

  playActiveVideo() {
    this.updateActiveVideo();

    if (!this.activeVideo) return;

    this.activeVideo.play().catch(() => {});
  }

  pauseVideos() {
    this.querySelectorAll("video").forEach((v) => {
      try { v.pause(); } catch (e) {}
    });
  }

  updateActiveVideo() {
    // Find candidates inside the modal
    // Prefer explicit desktop/mobile selectors if they exist
    this.desktopVideo =
      this.querySelector(".js-desktop-video") ||
      this.querySelector("video");

    this.mobileVideo =
      this.querySelector(".js-mobile-video");

    // Pick active by breakpoint, fallback to whatever exists
    if (window.innerWidth < IaModalVideo.BREAKPOINT && this.mobileVideo) {
      this.activeVideo = this.mobileVideo;
    } else {
      this.activeVideo = this.desktopVideo;
    }
  }
}

if (!customElements.get('ia-modal-video')) {
  customElements.define('ia-modal-video', IaModalVideo);
}


class IaModalGallery extends IaDrawer {
  constructor() {
    super();
    this._boundHandleArrowKeys = this.handleArrowKeys.bind(this);
  }

  toggleDrawer(event) {
    const opener = event.currentTarget;
    const idx = opener?.dataset?.galleryIndex;
    this.startGalleryIndex = idx != null ? Number(idx) : 0;

    super.toggleDrawer(event);
  }

  open() {
    super.open();

    const index = Number.isFinite(this.startGalleryIndex)
      ? this.startGalleryIndex
      : 0;

    const tryMove = () => {
      const galleryEl = this.querySelector("ia-product-gallery");
      if (!galleryEl) return false;

      // Wait until Keen is initialized inside the custom element
      if (!galleryEl.mainSlider) return false;

      galleryEl.goToSlide(index);
      return true;
    };

    requestAnimationFrame(() => {
      if (tryMove()) return;
      requestAnimationFrame(tryMove); // small fallback
    });
    
    this.preloadGalleryImages();
    this.attachArrowKeyListeners();
  }

  close() {
    super.close();
    this.startGalleryIndex = undefined;
    // Reset zoom on all zoomed images (currently inside modal only)
    this.querySelectorAll("ia-image-zoom.is-zoomed").forEach((z) => z.reset?.());
    this.detachArrowKeyListeners();
  }

  handleArrowKeys(event) {
    if (!this.isOpen) return;

    const galleryEl = this.querySelector("ia-product-gallery");
    if (!galleryEl || !galleryEl.mainSlider) return;

    // Only handle arrow keys if not typing in an input/textarea
    const activeElement = document.activeElement;
    const isInputFocused = activeElement && (
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.isContentEditable
    );

    if (isInputFocused) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      galleryEl.prev();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      galleryEl.next();
    }
  }

  attachArrowKeyListeners() {
    document.addEventListener('keydown', this._boundHandleArrowKeys);
  }

  detachArrowKeyListeners() {
    document.removeEventListener('keydown', this._boundHandleArrowKeys);
  }

  // preload images - fix for iOS / safari loading issue
  preloadGalleryImages() {
    const imgs = this.querySelectorAll('ia-product-gallery img.ia-gallery__slider-image');
  
    imgs.forEach((img) => {
      img.loading = "eager";
      img.fetchPriority = "high";
  
      if (img.decode) {
        img.decode().catch(() => {});
      }
    });
  }
}

if (!customElements.get("ia-modal-gallery")) {
  window.iaTheme = window.iaTheme || {};

  if (!window.iaTheme._drawerDomUpdatedListenerAttached) {
    window.iaTheme._drawerDomUpdatedListenerAttached = true;

    document.addEventListener("ia:dom-updated", (e) => {
      const ids = e.detail?.drawerIds;

      if (Array.isArray(ids) && ids.length) {
        ids.forEach((id) => {
          document
            .querySelector(`[data-drawer-id="${CSS.escape(id)}"]`)
            ?.refreshTriggers?.();
        });
        return;
      }

      document
        .querySelectorAll("[data-drawer-id]")
        .forEach((drawer) => drawer.refreshTriggers?.());
    });
  }

  customElements.define("ia-modal-gallery", IaModalGallery);
}
